import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface MCQ {
  id: string;
  topic_id: string | null;
  stem: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string | null;
  option_e: string | null;
  correct_option: string;
  explanation: string | null;
  difficulty: number | null;
  tags: string[] | null;
  review_status: string | null;
  last_reviewed_at: string | null;
  lifecycle_status: string | null;
  retired_at: string | null;
  retired_reason: string | null;
  created_at: string;
  updated_at: string;
  exam_labels?: Array<{
    id: string;
    name: string;
    slug: string;
    color: string;
  }>;
}

/**
 * Custom hook to fetch ALL MCQs from the database using efficient pagination
 * Overcomes the 1000 MCQ limit by fetching in batches
 */
export function useAllMcqs() {
  return useQuery({
    queryKey: ["all-mcqs"],
    queryFn: async () => {
      let allMcqs: MCQ[] = [];
      let offset = 0;
      const limit = 1000;

      while (true) {
        const { data, error } = await supabase
          .from("mcqs")
          .select(`
            *,
            mcq_exam_labels(
              label:exam_labels(id, name, slug, color)
            )
          `)
          .order("created_at", { ascending: false })
          .range(offset, offset + limit - 1);

        if (error) throw error;
        if (!data || data.length === 0) break;

        // Transform the nested structure to flat exam_labels array
        const transformedData = data.map((mcq: any) => {
          const labels = mcq.mcq_exam_labels
            ?.map((mel: any) => mel.label)
            .filter((l: any) => l !== null) || [];
          
          const { mcq_exam_labels, ...rest } = mcq;
          return {
            ...rest,
            exam_labels: labels
          };
        });

        allMcqs = [...allMcqs, ...transformedData];

        // If we got less than the limit, we've reached the end
        if (data.length < limit) break;
        
        offset += limit;
      }

      return allMcqs;
    },
    // Cache for 5 minutes to avoid unnecessary refetches
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Custom hook to fetch all unique tags from ALL MCQs
 */
export function useAllMcqTags() {
  return useQuery({
    queryKey: ["all-mcq-tags"],
    queryFn: async () => {
      let allTags: string[] = [];
      let offset = 0;
      const limit = 1000;

      while (true) {
        const { data, error } = await supabase
          .from("mcqs")
          .select("tags")
          .range(offset, offset + limit - 1);

        if (error) throw error;
        if (!data || data.length === 0) break;

        // Extract and deduplicate tags
        data.forEach((row) => {
          (row.tags || []).forEach((tag) => {
            if (!allTags.includes(tag)) {
              allTags.push(tag);
            }
          });
        });

        if (data.length < limit) break;
        offset += limit;
      }

      return allTags.sort();
    },
    staleTime: 5 * 60 * 1000,
  });
}
